import './date.css';

import _ from 'lodash';
import {Portal} from 'react-portal';
import PropTypes from 'prop-types';
import React, {useEffect, useRef, useState} from 'react';
import { faAngleLeft, faAngleRight} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {usePopper} from 'react-popper';
import InputMask from "react-input-mask";
import Calendar from 'react-calendar';
import TextBox from './TextBox';
import useGlobalOkDialog from '../hooks/useGlobalOkDialog';
import useSystemSettings from '../hooks/useSystemSettings';
import useMemento from '../hooks/useMemento';
import calendarIcon from '../img/calendar-red-top.png';
import {addMonths, dateTextToIso, formatDate, getLongDateFromIso, isoToDate, jsDateToIso, now, nowDate,} from '../dateUtils';
import moment from 'moment';
import useSuntekSettings from '../hooks/useSuntekSettings';

const currentYear = `${new Date().getFullYear()}`;
const currentDate = getLongDateFromIso(nowDate());

const DayNames = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
];

//const today = DayNames[moment(currentDate).day()];    //<240315.dwshin. remove warning>
const today = DayNames[moment().day()];

function onlyYearPartIsMissing(dateParam:string, format:string) {
  let date = _.cloneDeep(dateParam);

  if (!date || !format) {
    return false;
  }

  // This operation makes sense only for date formats that ends with 'yyyy';
  if (!format.toLowerCase().endsWith('-yyyy')) {
    return false;
  }

  // Fill remaining dateParts with blanks for easier calculation
  if (date.length < format.length) {
    const missingElements = format.length - date.length;
    for (let i = 0; i < missingElements; ++i) {
      date = `${date}_`;
    }
  }

  const currentYearPositionStart = format.search(/yyyy/i);
  if (currentYearPositionStart < 0) {
    return false;
  }

  if ((currentYearPositionStart + 4) > date.length) {
    return false;
  }

  // Check if only year part is missing
  for (let i = 0; i < date.length; ++i) {
    if (i >= currentYearPositionStart && i < (currentYearPositionStart + 4)) {
      // year characters must not be numbers
      if (!isNaN(parseInt(date[i]))) {
        return false;
      }
    } else if (format[i] === '-') {
      // if format has '-' at that position, date must also
      if (date[i] !== '-') {
        return false;
      }
    } else {
      // remaining part of the date must be filled with numbers
      if (isNaN(parseInt(date[i]))) {
        return false;
      }
    }
  }

  return true;
}

function applyCurrentYearToValue(date:string, format:string) {
  return `${date}${currentYear}`;
}

export type DateHandle = {
  setOpen: (x:boolean) => void,
};

type DateProps = {
  dateId?: string|undefined,
  value: string|null|undefined,
  onChange?: (x:string|null) => void,
  onEnter?: VoidFunction|undefined,
  disabled?: boolean,
  noRangeCheck?: boolean,
  startDateCheck?:boolean,
  noSetLastValidDateWhenOutOfRange?: boolean,
  noborder?: boolean,
  nopopup?: boolean,
  hideIcon?: boolean,
  validationFor?: string|undefined,
  onTabNext?: (() => boolean)|undefined,
  onTabPrevious?: (() => boolean)|undefined,
  onCalendarSelect?: VoidFunction|undefined,
  iconOffset?: string|undefined,
  formatString?: string|undefined,
  onBlur?: (() => void) | undefined,
  fireBlur?: boolean,
  name?:string|undefined,
  showPlaceHolder?:boolean,
  style?:any,
  doNotCheckRange?: boolean,
};

const IfmsDateImpl: React.ForwardRefRenderFunction<DateHandle, DateProps> = (
  {
    value,
    onChange = () => {},
    onEnter = undefined,
    disabled = false,
    onTabNext = undefined,
    onTabPrevious = undefined,
    dateId = undefined,
    onCalendarSelect = undefined,
    noRangeCheck = false,
    startDateCheck = false,
    noSetLastValidDateWhenOutOfRange = false,
    noborder = false,
    nopopup = false,
    hideIcon = false,
    validationFor = undefined,
    iconOffset = undefined,
    formatString = '',
    onBlur = undefined,
    fireBlur = false,
    name=undefined,
    showPlaceHolder = true,
    style={},
    doNotCheckRange = false, 
  },
  ref,
) => {
  React.useImperativeHandle(ref, () => ({
    setOpen,
  }));

  const dark = false;
  const suntekSettings = useSuntekSettings();
  const systemSettings = useSystemSettings();
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const format = formatString || suntekSettings.company?.dateFormat.toUpperCase() || '';

  const inputId = !!dateId ? dateId : _.uniqueId('ifms-date-');

  const [textValue, setTextValue] = useState(''); // Represents formatted value, like 12-28-2021 (MM-DD-YYYY)
  const [isoValue, setIsoValue, memento] = useMemento(null);
  const [open, _setOpen] = useState(false);
  const openRef = useRef(false);
  const calendarContainerRef = useRef<HTMLDivElement|null>(null);
  const inputContainerRef = useRef<HTMLDivElement|null>(null);
  const iconRef = useRef<HTMLImageElement|null>(null);
  
  const [referenceElement, setReferenceElement] = useState<HTMLInputElement|null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement|null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement|null>(null);
  const [activeStartDate, setActiveStartDate] = useState(new Date());
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: 'arrow', options: { element: arrowElement } }],
    placement: 'right-start',
    strategy: 'fixed'
  });




  let mask = format.replace(/m/g, '9').replace(/M/g, '9').replace(/y/g, '9').replace(/Y/g, '9').replace(/d/g, '9').replace(/D/g, '9');
  const placeholder = format.replace(/m/g, ' ').replace(/M/g, ' ').replace(/y/g, ' ').replace(/d/g, ' ').replace(/D/g, ' ');

  if(formatString) {
    mask = format.replace(/m/g, 'a').replace(/M/g, 'a').replace(/y/g, '9').replace(/Y/g, '9').replace(/d/g, '9').replace(/D/g, '9');
  }

  function setOpen(v:boolean) {
    openRef.current = v;
    if(textValue === ''){
       setActiveStartDate(new Date());
    }else{
       setActiveStartDate(new Date(value??''));
    }
    _setOpen(v);
  }

  useEffect(() => {
    mapValueToState();

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, format]);
  

  function onKeyUp(e:React.KeyboardEvent) {
    if (e.key === 'Enter' && onEnter) {
      setOpen(false);
      mapValueToState();
      onEnter();
    }
  }

  function isOutOfRange(iso:string|null) {
    if (!iso) {
      return false;
    }

    if (noRangeCheck) {
      return false;
    }

    if(startDateCheck){
      return false;
    }

    if (!systemSettings.intervalOfInputDate) {
      return false;
    }
    
    return iso > addMonths(now(),systemSettings.intervalOfInputDate) || iso < addMonths(now(),-systemSettings.intervalOfInputDate);
  }

  async function onChangeRaw(e:React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    if (value === '' || value === placeholder) {
      onChange(null);
      setTextValue(value);
    } else {
      const formatResult = dateTextToIso(value, format);
      if (formatResult.valid) {
        if (isOutOfRange(formatResult.iso) && !doNotCheckRange) {
          if (!noSetLastValidDateWhenOutOfRange) {
            const lastValid = memento();
            onChange(lastValid);
          }
          else
            onChange(formatResult.iso);
          
          await showGlobalOkDialog(<p>The entered date is out of range</p>);

          if (referenceElement) {
            referenceElement.focus();
            referenceElement.select();
          }
          return;

        } else if (startDateCheck){
          onChange(formatResult.iso);
          const startDateYear = suntekSettings.accounting?.startDateYear ?? '';
          const startDateMonth = suntekSettings.accounting?.startDateMonth ?? '';

          const startDate = startDateYear && startDateMonth ? moment(new Date(
              Number(startDateYear),
              Number(startDateMonth) - 1, 1)).format("YYYY-MM-DDTHH:mm:ss") : moment(new Date(2000, 0, 1)).format("YYYY-MM-DDTHH:mm:ss");

              if (startDate > (formatResult.iso ?? '')) {
                onChange(startDate);
                let errMsg = `The entered date is smaller than the start date of new accounting.\n(The start date of new accounting is on ${formatDate(startDate)})`;

                await showGlobalOkDialog(<p>{errMsg}</p>);
                
              }

              if (referenceElement) {
                referenceElement.focus();
                referenceElement.select();
              }

          return;

        }else {
          onChange(formatResult.iso);
        }
      }
      setTextValue(value);
    }
  }

  function mapValueToState() {
    const textValue = formatDate(value, format);
    setTextValue(textValue);
    setIsoValue(value);
  }

  function handleClickOutside(e:MouseEvent) {
    const target = e.target as Node|null;

    if (openRef.current && inputContainerRef && inputContainerRef.current && calendarContainerRef && calendarContainerRef.current) {
      const shouldClose = !inputContainerRef.current.contains(target) && !calendarContainerRef.current.contains(target);

      if (shouldClose) {
        setOpen(false);
      }
    }
  }

  function handleIconClick() {
    if (referenceElement) { 
      referenceElement.focus();
      referenceElement.select();
    }
    setOpen(true);
  }

  function handleFocus() {
    // Always show icon
    
    // if (iconRef && iconRef.current) {
    //   iconRef.current.style.display = 'none';
    // }
  }

  function handleBlur() {
    // Always show icon

    // if (iconRef && iconRef.current) {
    //   iconRef.current.style.display = 'block';
    // }

    if (onlyYearPartIsMissing(textValue, format)) {
      const valueWithCurrentYear = applyCurrentYearToValue(textValue, format);
      const formatResult = dateTextToIso(valueWithCurrentYear, format);

      if (!formatResult.valid || isOutOfRange(formatResult.iso)) {
        if (referenceElement) {
          referenceElement.focus();
          referenceElement.select();
        }
        mapValueToState();
      } else {
        onChange(formatResult.iso);
      }
    }

    mapValueToState();
    if(onBlur) onBlur();
  }

  async function handleCalendarChange(e: Date) {
    const iso = jsDateToIso(e);
    setOpen(false);
    if (isOutOfRange(iso) && !doNotCheckRange) {
      if (!noSetLastValidDateWhenOutOfRange) {
        const lastValid = memento();
        onChange(lastValid);
      }
      else
        onChange(iso);

      await showGlobalOkDialog(<p>The entered date is out of range</p>);

      if (referenceElement) {
        referenceElement.focus();
        referenceElement.select();
      }
      return;

    } else if (startDateCheck){
      onChange(iso);

      const startDateYear = suntekSettings.accounting?.startDateYear ?? '';
      const startDateMonth = suntekSettings.accounting?.startDateMonth ?? '';

      const startDate = startDateYear && startDateMonth ? moment(new Date(
          Number(startDateYear),
          Number(startDateMonth) - 1, 1)).format("YYYY-MM-DDTHH:mm:ss") : moment(new Date(2000, 0, 1)).format("YYYY-MM-DDTHH:mm:ss");

          if (startDate > (iso ?? '')) {
            onChange(startDate);
            let errMsg = `The entered date is smaller than the start date of new accounting.\n(The start date of new accounting is on ${formatDate(startDate)})`;

            await showGlobalOkDialog(<p>{errMsg}</p>);
            
          }

          if (referenceElement) {
            referenceElement.focus();
            referenceElement.select();
          }

      return;

    }else {
      onChange(iso);
    }
    if (onCalendarSelect) {
      onCalendarSelect();
    }
 
  }

  function processTabNext(e:React.KeyboardEvent) {
    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        setOpen(false);
        mapValueToState();
        if (e) {
          e.preventDefault();
        }
        return;
      }
    }
  }

  function processTabPrevious(e:React.KeyboardEvent) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        setOpen(false);
        mapValueToState();
        e.preventDefault();
        return;
      }
    }
  }

  function processSpecialKeys(e:React.KeyboardEvent) {
    if (!e || !e.keyCode) {
      return;
    }

    const tab = 9;
    const up = 38;
    const down = 40;

    if (e.keyCode === up || e.keyCode === down) {
      // const parentId = (e?.target as Element|null)?.parentElement?.parentElement?.id;
      // if (!!parentId) {
      //   channel.publish('ifms.control-navigation', {
      //     parentId,
      //     keyCode: e.keyCode
      //   });
      // }
      // setOpen(false);
      // mapValueToState();

      // e.preventDefault();
      // e.stopPropagation();

      return;
    }

    if (e.keyCode === tab) {
      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
    }

    if (fireBlur && e.key === 'Enter') {
      handleBlur();
    }
  }

  if (disabled) {
    return (
      <TextBox textId={inputId} noborder={noborder} name={name} disabled dark={dark} placeholder={showPlaceHolder ? format : ''} value={textValue} onChange={() => {}} />
    );
  } else {
    const isCurrentlyValid = textValue === '' || textValue === placeholder || dateTextToIso(textValue, format).valid;
    let className = isCurrentlyValid ? 'e-ifms-input e-outline ifms-tabable' : 'e-ifms-input e-outline e-ifms-invalid-input ifms-tabable';
    if (!disabled) {
      className = `${className} ifms-tabable-active`;
    }
    if (noborder) {
      className = `${className} ifms-noborder-input`;
    }

    const appliedPopperStyles = {
      ...styles.popper,
    };

    const iconStyle = iconOffset ? { right: iconOffset } : undefined;

    return (
      <>
        <div ref={inputContainerRef} className="ifms-date-placeholder">
          <InputMask style={style}
            // @ts-ignore:next-line
            maskChar={null}
            alwaysShowMask={false} mask={mask} value={textValue} onChange={onChangeRaw} onFocus={handleFocus} onBlur={handleBlur} onKeyUp={onKeyUp}>
            {(inputProps:any) => <input data-validationfor={validationFor} placeholder={showPlaceHolder ? format : ''} id={inputId} name={name} ref={setReferenceElement} type="text" {...inputProps} className={className} onKeyDown={processSpecialKeys} autoComplete="off" list="autocompleteOff" data-tooltip="-from-value" data-tooltip-onlywhencutoff />}
          </InputMask>
          {!hideIcon &&
            <img ref={iconRef} className="ifms-date-icon" style={iconStyle} height="17px" src={calendarIcon} alt="Date" onClick={handleIconClick} />
          }
        </div>
        {(open && !nopopup) &&
          <Portal node={document && document.getElementById('ifms-app-poppers')}>
            <div ref={setPopperElement} className="ifms-date-box" style={appliedPopperStyles} {...attributes.popper} tabIndex={5000}>
              <div ref={calendarContainerRef}>
                <div style={{textAlign:'center', fontSize:'15px', marginBottom:'5px'}}>
                  <button className="calendar-button" onClick={()=>{setActiveStartDate(new Date());}}>{today}, {currentDate}</button>
                </div>
                <Calendar
                  value={isoToDate(isoValue)}
                  onChange={handleCalendarChange}
                  locale="en-US"
                  nextLabel={<FontAwesomeIcon icon={faAngleRight} />}
                  prevLabel={<FontAwesomeIcon icon={faAngleLeft} />}
                  next2Label={null /*<FontAwesomeIcon icon={faAngleDoubleRight} />*/}
                  prev2Label={null /*<FontAwesomeIcon icon={faAngleDoubleLeft} />*/}
                  activeStartDate={activeStartDate}
                  onActiveStartDateChange={({activeStartDate}) => {setActiveStartDate(activeStartDate);}}
                />
                <div style={{textAlign:'center', marginTop:'10px'}}>
                  <button className="calendar-button" onClick={()=>{onChange(null); setTextValue(''); setOpen(false);}}>clear</button>
                </div>
                <div ref={setArrowElement} style={styles.arrow} />
              </div>
            </div>
          </Portal>
        }
      </>
    );
  }
};

const IfmsDate = React.forwardRef(IfmsDateImpl);

IfmsDate.defaultProps = {
  onChange: () => {},
  disabled: false,
  noRangeCheck: false,
  startDateCheck:false,
  noSetLastValidDateWhenOutOfRange: true,
  noborder: false,
  nopopup: false,
  hideIcon: false,
  fireBlur: false,
  style: {},
};

IfmsDate.propTypes = {
  onChange: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  value: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  dateId: PropTypes.string,
  onCalendarSelect: PropTypes.func,
  noRangeCheck: PropTypes.bool.isRequired,
  startDateCheck: PropTypes.bool.isRequired,
  noSetLastValidDateWhenOutOfRange: PropTypes.bool.isRequired,
  noborder: PropTypes.bool.isRequired,
  nopopup: PropTypes.bool.isRequired,
  hideIcon: PropTypes.bool.isRequired,
  validationFor: PropTypes.string,
  iconOffset: PropTypes.string,
  formatString: PropTypes.string,
  fireBlur: PropTypes.bool,
  style: PropTypes.any,
};


export default IfmsDate;
